// =====================================================
// PAGE ADMIN: GESTION RENDEZ-VOUS PSY
// =====================================================

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { AdminLayout } from '../../../components/layout/AdminLayout';
import { Search, Clock, ArrowUpDown, Calendar } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { formatAmountFCFA } from '../../../services/psyBookingService';
import { useToast } from '../../../components/ui/use-toast';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import { useCheckPermissions } from '../../../middleware/checkPermissions';

type SortField = 'date' | 'practitioner' | 'patient' | 'status' | 'amount';
type SortDirection = 'asc' | 'desc';

export default function AdminPsyAppointments() {
  useCheckPermissions(['admin']);
  const { toast } = useToast();
  const [appointments, setAppointments] = useState<any[]>([]);
  const [filteredAppointments, setFilteredAppointments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [stats, setStats] = useState({
    total: 0,
    scheduled: 0,
    confirmed: 0,
    completed: 0,
    cancelled: 0,
    totalRevenue: 0,
  });

  useEffect(() => {
    loadAppointments();
  }, []);

  useEffect(() => {
    applyFilters();
    calculateStats();
  }, [searchTerm, statusFilter, appointments, sortField, sortDirection]);

  const loadAppointments = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('psy_appointments')
        .select(`
          *,
          practitioner:psy_practitioners(full_name, email),
          patient:psy_patients(full_name, email)
        `)
        .order('scheduled_at', { ascending: false });

      if (error) throw error;
      setAppointments(data || []);
    } catch (error) {
      console.error('Erreur:', error);
      toast({
        title: 'Erreur',
        description: 'Erreur lors du chargement',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection(field === 'date' ? 'desc' : 'asc');
    }
  };

  const applyFilters = () => {
    let filtered = [...appointments];

    // Recherche
    if (searchTerm) {
      filtered = filtered.filter(
        (a) =>
          a.practitioner?.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          a.patient?.full_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filtre statut
    if (statusFilter !== 'all') {
      filtered = filtered.filter((a) => a.status === statusFilter);
    }

    // Tri
    filtered.sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case 'date':
          comparison = new Date(a.scheduled_at).getTime() - new Date(b.scheduled_at).getTime();
          break;
        case 'practitioner':
          comparison = (a.practitioner?.full_name || '').localeCompare(b.practitioner?.full_name || '');
          break;
        case 'patient':
          comparison = (a.patient?.full_name || '').localeCompare(b.patient?.full_name || '');
          break;
        case 'status':
          const statusOrder = { scheduled: 1, confirmed: 2, completed: 3, cancelled: 4, no_show: 5 };
          comparison = (statusOrder[a.status as keyof typeof statusOrder] || 6) - 
                       (statusOrder[b.status as keyof typeof statusOrder] || 6);
          break;
        case 'amount':
          comparison = (a.amount_paid || 0) - (b.amount_paid || 0);
          break;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });

    setFilteredAppointments(filtered);
  };

  const calculateStats = () => {
    const total = appointments.length;
    const scheduled = appointments.filter((a) => a.status === 'scheduled').length;
    const confirmed = appointments.filter((a) => a.status === 'confirmed').length;
    const completed = appointments.filter((a) => a.status === 'completed').length;
    const cancelled = appointments.filter((a) => a.status === 'cancelled').length;
    const totalRevenue = appointments
      .filter((a) => a.status === 'completed')
      .reduce((sum, a) => sum + (a.amount_paid || 0), 0);

    setStats({ total, scheduled, confirmed, completed, cancelled, totalRevenue });
  };

  const getStatusBadge = (status: string) => {
    const badges: Record<string, { className: string; label: string }> = {
      scheduled: { className: 'bg-blue-400/20 text-blue-400 border-blue-400/20', label: 'Planifié' },
      confirmed: { className: 'bg-green-400/20 text-green-400 border-green-400/20', label: 'Confirmé' },
      completed: { className: 'bg-gray-400/20 text-gray-400 border-gray-400/20', label: 'Terminé' },
      cancelled: { className: 'bg-red-400/20 text-red-400 border-red-400/20', label: 'Annulé' },
      no_show: { className: 'bg-orange-400/20 text-orange-400 border-orange-400/20', label: 'Absent' },
    };

    const badge = badges[status] || badges.scheduled;

    return (
      <Badge className={badge.className}>
        {badge.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center p-12">
          <Clock className="mr-2 h-8 w-8 animate-spin text-yellow-400" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white mb-1">Gestion des Rendez-vous</h1>
            <p className="text-gray-400">Suivez tous les rendez-vous psychologiques</p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-6 gap-6 mb-6">
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
            <div className="text-sm text-gray-400 mb-1">Total</div>
            <div className="text-2xl font-bold text-white">{stats.total}</div>
          </div>

          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
            <div className="text-sm text-gray-400 mb-1">Planifiés</div>
            <div className="text-2xl font-bold text-blue-400">{stats.scheduled}</div>
          </div>

          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
            <div className="text-sm text-gray-400 mb-1">Confirmés</div>
            <div className="text-2xl font-bold text-green-400">{stats.confirmed}</div>
          </div>

          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
            <div className="text-sm text-gray-400 mb-1">Terminés</div>
            <div className="text-2xl font-bold text-gray-400">{stats.completed}</div>
          </div>

          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
            <div className="text-sm text-gray-400 mb-1">Annulés</div>
            <div className="text-2xl font-bold text-red-400">{stats.cancelled}</div>
          </div>

          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
            <div className="text-sm text-gray-400 mb-1">Revenus</div>
            <div className="text-2xl font-bold text-green-400">
              {formatAmountFCFA(stats.totalRevenue)}
            </div>
          </div>
        </div>

        {/* Filtres */}
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <Input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Rechercher par praticien ou patient..."
                className="pl-10 bg-white/5 border-white/10 text-white placeholder-gray-400"
              />
            </div>

            <div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-4 py-2 bg-white/5 border border-white/10 text-white rounded-lg focus:ring-2 focus:ring-yellow-500/50"
              >
                <option value="all" className="bg-gray-900">Tous les statuts</option>
                <option value="scheduled" className="bg-gray-900">Planifiés</option>
                <option value="confirmed" className="bg-gray-900">Confirmés</option>
                <option value="completed" className="bg-gray-900">Terminés</option>
                <option value="cancelled" className="bg-gray-900">Annulés</option>
              </select>
            </div>
          </div>
        </div>

        {/* Liste */}
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    <button
                      onClick={() => handleSort('date')}
                      className="flex items-center gap-2 hover:text-white transition-colors"
                    >
                      Date & Heure
                      <ArrowUpDown size={14} className={sortField === 'date' ? 'text-yellow-400' : ''} />
                    </button>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    <button
                      onClick={() => handleSort('practitioner')}
                      className="flex items-center gap-2 hover:text-white transition-colors"
                    >
                      Praticien
                      <ArrowUpDown size={14} className={sortField === 'practitioner' ? 'text-yellow-400' : ''} />
                    </button>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    <button
                      onClick={() => handleSort('patient')}
                      className="flex items-center gap-2 hover:text-white transition-colors"
                    >
                      Patient
                      <ArrowUpDown size={14} className={sortField === 'patient' ? 'text-yellow-400' : ''} />
                    </button>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    <button
                      onClick={() => handleSort('status')}
                      className="flex items-center gap-2 hover:text-white transition-colors"
                    >
                      Statut
                      <ArrowUpDown size={14} className={sortField === 'status' ? 'text-yellow-400' : ''} />
                    </button>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    <button
                      onClick={() => handleSort('amount')}
                      className="flex items-center gap-2 hover:text-white transition-colors"
                    >
                      Montant
                      <ArrowUpDown size={14} className={sortField === 'amount' ? 'text-yellow-400' : ''} />
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {filteredAppointments.map((appointment, index) => (
                  <tr key={appointment.id} className={`hover:bg-white/10 ${index % 2 === 0 ? 'bg-white/5' : 'bg-white/10'}`}>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-white">
                        {new Date(appointment.scheduled_at).toLocaleDateString('fr-FR')}
                      </div>
                      <div className="text-sm text-gray-400">
                        {new Date(appointment.scheduled_at).toLocaleTimeString('fr-FR', {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-white">
                        {appointment.practitioner?.full_name || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-white">
                        {appointment.patient?.full_name || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4">{getStatusBadge(appointment.status)}</td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-white">
                        {formatAmountFCFA(appointment.amount_paid || 0)}
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredAppointments.length === 0 && (
                  <tr>
                    <td colSpan={5} className="text-center py-12 text-gray-400">
                      <Calendar className="h-12 w-12 text-gray-500 mx-auto mb-4" />
                      <p>Aucun rendez-vous trouvé</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
